import React, { useState } from 'react';
import {
  Button,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Divider,
  Tab,
  Tabs,
  Link,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../contexts/AuthContext';
import { getAuthErrorMessage } from '../utils/authErrorMessage';

// タブパネルのProps
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// タブパネルコンポーネント
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const SHARED_STORAGE_ERROR_MESSAGES: Record<string, string> = {
  'auth/web-storage-unsupported':
    'ブラウザのストレージ機能が無効になっています。プライベートブラウズを解除するかCookieを許可してください',
};

const Login: React.FC = () => {
  const {
    signIn,
    loginWithEmailAndPassword,
    registerWithEmailAndPassword,
    sendPasswordReset,
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // フォーム入力
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);

  // タブ変更ハンドラー
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setShowResetForm(false);
  };

  // Googleログインハンドラー
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signIn();
    } catch (err: unknown) {
      console.error('Google sign in failed:', err);
      setError(
        getAuthErrorMessage(
          err,
          {
            ...SHARED_STORAGE_ERROR_MESSAGES,
            'auth/popup-closed-by-user':
              'ログインがキャンセルされました（ウィンドウが閉じられました）',
            'auth/popup-blocked':
              'ポップアップがブロックされました。ブラウザの設定でポップアップを許可してください',
            'auth/cancelled-popup-request':
              'ログイン処理が中断されました。再度お試しください',
            'auth/unauthorized-domain':
              'このドメインからのログインは許可されていません（Firebaseの設定をご確認ください）',
            'auth/operation-not-allowed':
              'Googleログインが有効になっていません（Firebaseの設定をご確認ください）',
            'auth/network-request-failed':
              'ネットワークエラーが発生しました。接続を確認して再度お試しください',
            'auth/internal-error':
              '認証サービスで内部エラーが発生しました。しばらく時間をおいて再度お試しください',
            'auth/missing-or-invalid-nonce':
              '認証ステートの確認に失敗しました。iPhone/Safariをお使いの場合はプライベートブラウズを解除するか、メールアドレスでのログイン・新規登録をお試しください',
          },
          'Googleログインに失敗しました。もう一度お試しください。'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // メール/パスワードログインハンドラー
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await loginWithEmailAndPassword(email, password);
    } catch (err: unknown) {
      console.error('Email login failed:', err);
      setError(
        getAuthErrorMessage(
          err,
          {
            ...SHARED_STORAGE_ERROR_MESSAGES,
            'auth/user-not-found':
              'メールアドレスまたはパスワードが正しくありません',
            'auth/wrong-password':
              'メールアドレスまたはパスワードが正しくありません',
            'auth/too-many-requests':
              'ログイン試行回数が多すぎます。しばらく時間をおいてから再度お試しください',
          },
          'ログインに失敗しました。'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // 新規ユーザー登録ハンドラー
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await registerWithEmailAndPassword(email, password, displayName);
      // 登録成功後、自動的にログインタブに切り替え
      setTabValue(0);
    } catch (err: unknown) {
      console.error('Registration failed:', err);
      setError(
        getAuthErrorMessage(
          err,
          {
            ...SHARED_STORAGE_ERROR_MESSAGES,
            'auth/email-already-in-use':
              'このメールアドレスは既に使用されています',
            'auth/invalid-email': '有効なメールアドレスを入力してください',
            'auth/weak-password':
              'パスワードが弱すぎます。より強力なパスワードを設定してください',
          },
          '登録に失敗しました。'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // パスワードリセットハンドラー
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await sendPasswordReset(email);
      setError(
        'パスワードリセットのリンクを送信しました。メールを確認してください'
      );
    } catch (err: unknown) {
      console.error('Password reset failed:', err);
      setError(
        getAuthErrorMessage(
          err,
          {
            ...SHARED_STORAGE_ERROR_MESSAGES,
            'auth/user-not-found':
              'このメールアドレスに関連するアカウントが見つかりません',
            'auth/invalid-email': '有効なメールアドレスを入力してください',
          },
          'パスワードリセットに失敗しました。'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: 500,
        mx: 'auto',
        mt: 4,
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        野球スコアアプリ
      </Typography>

      <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
        データを保存したり、過去の試合を見るには、ログインまたは登録してください。
      </Typography>

      {error && (
        <Alert
          severity={error.includes('送信しました') ? 'success' : 'error'}
          sx={{ width: '100%', mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            variant="fullWidth"
          >
            <Tab label="ログイン" />
            <Tab label="新規登録" />
          </Tabs>
        </Box>

        {/* ログインタブ */}
        <TabPanel value={tabValue} index={0}>
          {!showResetForm ? (
            <form onSubmit={handleEmailLogin}>
              <TextField
                label="メールアドレス"
                type="email"
                fullWidth
                variant="outlined"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                }}
                disabled={loading}
              />
              <TextField
                label="パスワード"
                type="password"
                fullWidth
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                }}
                disabled={loading}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'ログイン'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                パスワードをリセットするためのリンクが記載されたメールを送信します。
              </Typography>
              <TextField
                label="メールアドレス"
                type="email"
                fullWidth
                variant="outlined"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                }}
                disabled={loading}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2 }}
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  'パスワードをリセット'
                )}
              </Button>
              <Button
                variant="text"
                color="primary"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => setShowResetForm(false)}
                disabled={loading}
              >
                ログイン画面に戻る
              </Button>
            </form>
          )}

          {!showResetForm && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowResetForm(true)}
                disabled={loading}
              >
                パスワードをお忘れですか？
              </Link>
            </Box>
          )}

          <Divider sx={{ my: 3 }}>または</Divider>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            Googleでログイン
          </Button>
        </TabPanel>

        {/* 新規登録タブ */}
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handleRegister}>
            <TextField
              label="表示名（任意）"
              type="text"
              fullWidth
              variant="outlined"
              margin="normal"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              InputProps={{
                startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
              }}
              disabled={loading}
            />
            <TextField
              label="メールアドレス"
              type="email"
              fullWidth
              variant="outlined"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
              }}
              disabled={loading}
            />
            <TextField
              label="パスワード（6文字以上）"
              type="password"
              fullWidth
              variant="outlined"
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
              }}
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'アカウントを作成'}
            </Button>
          </form>

          <Divider sx={{ my: 3 }}>または</Divider>

          <Button
            variant="outlined"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            Googleで登録
          </Button>
        </TabPanel>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 1 }}
        >
          ※ログインすることで、あなたのデータが安全に保存されます。
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
        >
          ※ iPhone/Safari 等で Google
          ログイン・登録ができない場合は、メールアドレスでのご利用をおすすめします。
        </Typography>
      </Box>
    </Paper>
  );
};

export default Login;
